import jsPDF from "jspdf";
import "jspdf-autotable";
import config from "../common/Config";
import { axiosFetchProformaInvoice } from '../../utils/proformaInvoiceApi';

const generateUniqueFilename = (baseName, extension) => {
  const timestamp = new Date().toISOString().replace(/[-:.]/g, "");
  return `${baseName}_${timestamp}.${extension}`;
};

const printNewSalesInvoice = async (orderId) => {
  const token = localStorage.getItem('token');
  const accessToken = localStorage.getItem("accessToken");

  const safe = (val) => (val === null || val === undefined ? "" : val);

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    
    // If already in DD-MM-YYYY format, return as-is
    if (/^\d{2}-\d{2}-\d{4}$/.test(timestamp)) {
      return timestamp;
    }
    
    // Otherwise parse as timestamp
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${day}-${month}-${year}`;
  };

  try {
    const data = await axiosFetchProformaInvoice(orderId, token);

    if (
      !data.success ||
      !data.details.orderDetails ||
      data.details.orderDetails.length === 0
    ) {
      console.error("No order details found in the response.");
      return;
    }

    const orderDetails = data.details.orderDetails[0];
    const shopDetails = data.details.shop_details;
    const bundleDiscounts = data.details.bundle_discounts || [];

    // ✅ Box/piece qty counts
    const box_qty = data.details.product_details
      .filter(
        (item) =>
          item.quantity != "0.00" &&
          item.quantity != null &&
          item.order_type === "box",
      )
      .reduce((sum, item) => sum + parseFloat(item.quantity), 0);

    const piece_qty = data.details.product_details
      .filter(
        (item) =>
          item.quantity != "0.00" &&
          item.quantity != null &&
          item.order_type === "piece",
      )
      .reduce((sum, item) => sum + parseFloat(item.quantity), 0);

    // ✅ FIXED: subtotals already have discounts applied, sum them directly
    const totalstd = data.details.product_details
      .filter((item) => parseFloat(item.item_tax || 0) !== 0)
      .reduce((sum, item) => sum + parseFloat(item.subtotal || 0), 0);

    const totalzeroGoods = data.details.product_details
      .filter((item) => parseFloat(item.item_tax || 0) === 0)
      .reduce((sum, item) => sum + parseFloat(item.subtotal || 0), 0);

    // ✅ FIXED: VAT summed from item_tax
    const totalVAT = data.details.product_details.reduce(
      (sum, item) => sum + parseFloat(item.item_tax || 0),
      0,
    );

    const orderDiscount =
      parseFloat(data.details.orderDetails[0].order_discount) || 0;
    const orderDiscount1 =
      parseFloat(data.details.orderDetails[0].order_discount1) || 0;
    const over_all_discount =
      parseFloat(data.details.orderDetails[0].over_all_discount) || 0;
    const over_all_amount =
      parseFloat(data.details.orderDetails[0].over_all_amount) || 0;

    // ✅ FIXED: itemTotal = subtotals (already net of product discounts), no need to subtract VAT
    const itemTotal = totalstd + totalzeroGoods;

    // ✅ FIXED: totalAmount uses orderDiscount1 like proforma file
    const totalAmount =
      parseFloat(orderDetails.grand_total || 0) > 0
        ? parseFloat(orderDetails.grand_total)
        : itemTotal + totalVAT;

    const totalPromoDiscountFromBundle = bundleDiscounts.reduce(
      (sum, item) => sum + parseFloat(item.total_promo_discount || 0),
      0,
    );
    const totalPromoDiscountVatFromBundle = bundleDiscounts.reduce(
      (sum, item) => sum + parseFloat(item.total_promo_discount_vat || 0),
      0,
    );
    const totalPromoDiscountProduct = data.details.product_details.reduce(
      (sum, item) => {
        const promoDiscAmount = parseFloat(item.promo_disc_amount || 0);
        if (promoDiscAmount > 0) {
          return sum + promoDiscAmount;
        }
        const originalPrice = parseFloat(
          item.original_price || item.unit_price || 0,
        );
        const discountPrice = parseFloat(
          item.discount_price || item.unit_price || 0,
        );
        const quantity = parseFloat(item.quantity || 0);
        return sum + (originalPrice - discountPrice) * quantity;
      },
      0,
    );
    const totalPromoDiscount =
      totalPromoDiscountFromBundle > 0
        ? totalPromoDiscountFromBundle
        : parseFloat(orderDetails.total_promo_discount || 0) > 0
          ? parseFloat(orderDetails.total_promo_discount)
          : totalPromoDiscountProduct;
    const totalPromoDiscountVat =
      totalPromoDiscountVatFromBundle > 0
        ? totalPromoDiscountVatFromBundle
        : parseFloat(orderDetails.total_promo_discount_vat || 0);

    const doc = new jsPDF("portrait");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 5;
    let y = margin;

    const H2firstColumnWidth = pageWidth * 0.2;
    const H2secondColumnWidth = pageWidth * 0.55;
    const H2thirdColumnWidth = pageWidth * 0.25;

    const topMargin = 1;
    const logoUrl = `${config.BASE_URL}/assets/img/${accessToken}_logo.png`;
    const logoWidth = 35;
    const logoHeight = 35;

    const a = topMargin;
    doc.addImage(logoUrl, "PNG", margin, a, logoWidth, logoHeight);

    // Header - Shop Info (3 columns)
    const headerSections2 = [
      [
        // Empty first column (logo takes this space)
      ],
      [
        { text: `${safe(shopDetails.shop_name)}`, size: 13, bold: true },
        {
          text: `Unit 31, Silicon Business Centre, 28 Wadsworth Road,`,
          size: 8,
          bold: false,
        },
        { text: "Perivale, UB6 7JZ United Kingdom", size: 8, bold: false },
        {
          text: `Company No : ${safe(
            shopDetails.company_reg_no,
          )} | VAT No : ${safe(shopDetails.vat_no)}`,
          size: 8,
          bold: false,
        },
        { text: `Email : ${safe(shopDetails.email)}`, size: 8, bold: false },
        { text: `Email : tgschilledfoods@gmail.com`, size: 8, bold: false },
      ],
      [
        { text: `${safe(shopDetails.website)}`, size: 8, bold: false },
        {
          text: `SORT CODE : ${safe(shopDetails.sort_code)}`,
          size: 8,
          bold: false,
        },
        { text: `ACC NO : ${safe(shopDetails.bank_ac)}`, size: 8, bold: false },
        { text: `Tel: ${safe(shopDetails.phone)}`, size: 8, bold: false },
        { text: `Mob: 07480 672493`, size: 8, bold: false },
        { text: ``, size: 8, bold: false },
      ],
    ];

    // Header - Order & Customer Info (2 columns)
    const headerSections3 = [
      [
        { text: `Order : ${safe(orderDetails.reference_no)}`, size: 8, bold: false },
        {
          text: `Order Date: ${formatDate(orderDetails.date)}`,
          size: 8,
          bold: false,
        },
        {
          text: `Sale Status: ${safe(orderDetails.sale_status)}`,
          size: 8,
          bold: false,
        },
        { text: `Payment Method : `, size: 8, bold: false },
        {
          text: `Payment Status : ${orderDetails.payment_status ?? "Due"}`,
          size: 8,
          bold: false,
        },
      ],
      [
        { text: `${safe(orderDetails.company)}`, size: 8, bold: false },
        { text: `Phone No : ${safe(orderDetails.phone)}`, size: 8, bold: false },
        { text: `Email Id : ${safe(orderDetails.email)}`, size: 8, bold: false },
        { text: `Address  : ${safe(orderDetails.address)}`, size: 8, bold: false },
        {
          text: `${safe(orderDetails.city)} ,${safe(orderDetails.postal_code)}`,
          size: 8,
          bold: false,
        },
      ],
    ];

    const sectionWidth = (pageWidth - margin * 3) / 3;

    y = 7;

    const renderSections = (sections) => {
      let rowHeight = 0;
      let columnHeights = [];

      sections.forEach((section, index) => {
        let colStartX;
        if (index % 3 === 0) {
          colStartX = margin;
        } else if (index % 3 === 1) {
          colStartX = margin + H2firstColumnWidth;
        } else {
          colStartX = margin + H2firstColumnWidth + H2secondColumnWidth;
        }

        let sectionHeight = 0;

        section.forEach((item, i) => {
          doc.setFontSize(item.size);
          doc.setFont(
            item.bold ? "helvetica" : "helvetica",
            item.bold ? "bold" : "normal",
          );

          let maxWidth;
          if (index % 3 === 0) {
            maxWidth = H2firstColumnWidth - 5;
          } else if (index % 3 === 1) {
            maxWidth = H2secondColumnWidth - 5;
          } else {
            maxWidth = H2thirdColumnWidth - 5;
          }

          const textWidth = doc.getTextWidth(item.text);
          let lines = [];
          if (textWidth > maxWidth) {
            let words = item.text.split(" ");
            let line = "";
            words.forEach((word) => {
              const testLine = line + (line ? " " : "") + word;
              if (doc.getTextWidth(testLine) <= maxWidth) {
                line = testLine;
              } else {
                lines.push(line);
                line = word;
              }
            });
            lines.push(line);
          } else {
            lines.push(item.text);
          }

          lines.forEach((lineText, lineIndex) => {
            const lineHeight = 4;
            doc.text(
              lineText,
              colStartX + 5,
              y + 5 + i * lineHeight + lineIndex * lineHeight,
            );
            sectionHeight = y + i + lineIndex - 5;
          });
        });

        columnHeights.push(sectionHeight);

        if ((index + 1) % 3 === 0) {
          rowHeight = Math.max(...columnHeights);
          columnHeights = [];
          y += rowHeight + 12;
        }
      });

      doc.line(
        margin,
        y + 8,
        margin +
          H2firstColumnWidth +
          H2secondColumnWidth +
          H2thirdColumnWidth +
          1,
        y + 8,
      );

      y += -3;
    };

    const h2renderSections = (sections, topMargin = 15) => {
      y += topMargin;

      sections.forEach((section, index) => {
        const colStartX = margin + (index % 2) * H2firstColumnWidth;

        section.forEach((item, i) => {
          doc.setFontSize(item.size);
          doc.setFont(
            item.bold ? "helvetica" : "helvetica",
            item.bold ? "bold" : "normal",
          );
          doc.text(item.text, colStartX, y + i * 4);
        });

        if (index % 4 === 3) {
          y += 12;
        }
      });

      y += 20;
    };

    renderSections(headerSections2);
    h2renderSections(headerSections3);

    // Table columns
    const tableColumns = [
      { title: "Qty", dataKey: "qty" },
      { title: "Order Type", dataKey: "order_type" },
      { title: "Product", dataKey: "product" },
      { title: "Unit Price", dataKey: "unit_price" },
      { title: "Discount", dataKey: "discount" },
      { title: "VAT", dataKey: "vat" },
      { title: "Net Amount", dataKey: "net_amount" },
    ];

    const totalWidth = pageWidth - 2 * margin;
    const productWidth = totalWidth * 0.45;
    const orderTypeWidth = totalWidth * 0.1;
    const qtyWidth = totalWidth * 0.06;
    const unitPriceWidth = totalWidth * 0.1;
    const discountWidth = totalWidth * 0.08;
    const vatWidth = totalWidth * 0.1;
    const netAmountWidth = totalWidth * 0.1;

    const tableBody = data.details.product_details.map((item) => {
      const productLabel =
        `${safe(item.product_code)} - ${safe(item.product_name)}` +
        (item.promo_product ? ` ${safe(item.promo_product)}` : "") +
        (item.free_product ? ` ${safe(item.free_product)}` : "") +
        (item.ContractPrice ? ` ${safe(item.ContractPrice)}` : "");

      const promoDisc = parseFloat(item.promo_disc_amount || 0);
      const itemDisc = parseFloat(item.item_discount || 0);

      const rawDiscountType =
        item.productWiseDiscountType || item.discount_type || "amount";
      const discountType =
        String(rawDiscountType).toLowerCase() === "percentage"
          ? "percentage"
          : "amount";

        // Promo discount: show £ amount
      let discountDisplay = "";
      if (promoDisc > 0) {
        discountDisplay = `£ ${promoDisc.toFixed(2)}`;
      } else if (itemDisc > 0) {
          // Amount type: show £
        if (discountType === "percentage") {
          discountDisplay = `${parseFloat(item.discount || 0).toFixed(0)}%`;
        } else {
          discountDisplay = `£ ${itemDisc.toFixed(2)}`;
        }
      }

      const qtyDisplay =
        item.quantity !== null &&
        item.quantity !== undefined &&
        item.quantity !== ""
          ? parseInt(item.quantity, 10)
          : "";

      return [
        qtyDisplay,
        safe(item.order_type),
        productLabel,
        `£ ${parseFloat(item.unit_price || 0).toFixed(2)}`,
        discountDisplay,
        `£ ${parseFloat(item.item_tax || 0).toFixed(2)}`,
        `£ ${parseFloat(item.subtotal || 0).toFixed(2)}`,
      ];
    });

    // ✅ Summary rows
    tableBody.push([
      {
        content: "Item Total",
        colSpan: 6,
        styles: { halign: "right", fontStyle: "bold" },
      },
      { content: `£ ${itemTotal.toFixed(2)}` },
    ]);

    // tableBody.push([
    //   {
    //     content: "Vatable Total",
    //     colSpan: 6,
    //     styles: { halign: "right", fontStyle: "bold" },
    //   },
    //   { content: `£ ${totalzeroGoods.toFixed(2)}` },
    // ]);

    tableBody.push([
      {
        content: "Total Vat",
        colSpan: 6,
        styles: { halign: "right", fontStyle: "bold" },
      },
      { content: `£ ${totalVAT.toFixed(2)}` },
    ]);

    tableBody.push([
      {
        content: `Order Discount(${over_all_discount}%)`,
        colSpan: 6,
        styles: { halign: "right", fontStyle: "bold" },
      },
      { content: `£ ${over_all_amount.toFixed(2)}` },
    ]);

    if (bundleDiscounts.length > 0) {
    tableBody.push([
      {
        content: "Promo Discount",
        colSpan: 6,
        styles: { halign: "right", fontStyle: "bold" },
      },
      { content: `£ ${totalPromoDiscount.toFixed(2)}` },
    ]);

    tableBody.push([
      {
        content: "Promo Discount VAT",
        colSpan: 6,
        styles: { halign: "right", fontStyle: "bold" },
      },
      { content: `£ ${totalPromoDiscountVat.toFixed(2)}` },
    ]);
  }

    tableBody.push([
      {
        content: `Box : ${box_qty} PCs : ${piece_qty} `,
        colSpan: 5,
        styles: { halign: "left", fontSize: 8, fontStyle: "bold" },
      },
      {
        content: "Total Amount",
        styles: { halign: "right", fontSize: 8, fontStyle: "bold" },
      },
      { content: `£ ${totalAmount.toFixed(2)}` },
    ]);

    doc.autoTable({
      startY: y,
      head: [tableColumns.map((col) => col.title)],
      body: tableBody,
      theme: "grid",
      headStyles: {
        fillColor: "#f8f9fa",
        textColor: "#404040",
        fontSize: 8,
        valign: "middle",
        lineWidth: 0.3,
        lineColor: [0, 0, 0],
      },
      bodyStyles: {
        fontSize: 8,
        valign: "middle",
        lineWidth: 0.3,
        lineColor: [0, 0, 0],
      },
      margin: { top: 5, left: margin, right: margin },
      columnStyles: {
        0: { cellWidth: qtyWidth, halign: "center" },
        1: { cellWidth: orderTypeWidth, halign: "center" },
        2: { cellWidth: productWidth },
        3: { cellWidth: unitPriceWidth, halign: "right" },
        4: { cellWidth: discountWidth, halign: "right" },
        5: { cellWidth: vatWidth, halign: "right" },
        6: { cellWidth: netAmountWidth, halign: "right" },
      },
    });

    y = doc.autoTable.previous.finalY + 5;

    // ✅ Page numbers (copied from second file)
    const addPageNumbers = (doc) => {
      const pageCount = doc.internal.getNumberOfPages();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, {
          align: "center",
        });
      }
    };

    const addFooterSection = (footerSection) => {
      footerSection.forEach((section, index) => {
        const colStartX = margin + (index % 4) * sectionWidth;
        section.forEach((item, i) => {
          doc.setFontSize(item.size);
          doc.setFont(
            item.bold ? "helvetica" : "helvetica",
            item.bold ? "bold" : "normal",
          );
          doc.text(item.text, colStartX, y + i * 5);
        });
        if (index % 4 === 3) {
          y += 20;
        }
      });
      y += 25;
    };

    const addFooterWithSpaceCheck = (footerSection) => {
      const remainingSpace = pageHeight - y - margin;
      const requiredSpace = footerSection.length * 5 + 20;
      if (remainingSpace < requiredSpace) {
        doc.addPage();
        y = margin;
      }
      addFooterSection(footerSection);
    };

    const footerSections2 = [
      [
        {
          text: `Goods remain the property of ${safe(
            shopDetails.shop_name,
          )} LTD until invoice fully paid.`,
          size: 8,
          bold: true,
        },
        {
          text: "Good Received & Checked By : _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ No of Boxes Received : _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _",
          size: 8,
          bold: false,
        },
        {
          text: "Amount due to collect : _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ ",
          size: 8,
          bold: false,
        },
        {
          text: "Amount Paid : _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _:cash :Cheque : ",
          size: 8,
          bold: false,
        },
        {
          text: "Amount Outstanding : _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _:Driver Name : ",
          size: 8,
          bold: false,
        },
      ],
    ];

    addFooterWithSpaceCheck(footerSections2);

    const footerSections3 = [
      [{ text: `Note: ${safe(orderDetails.staff_note)}`, size: 8, bold: true }],
    ];

    addFooterWithSpaceCheck(footerSections3);

    // ✅ Add page numbers before printing
    addPageNumbers(doc);

    const filename = generateUniqueFilename("Invoice", "pdf");
    // doc.save(filename);
    doc.autoPrint();
    window.open(doc.output("bloburl"));
  } catch (error) {
    console.error("Error generating PDF:", error);
  }
};

export default printNewSalesInvoice;
